import { lazy } from "react";

// =====================================================================
// lazyWithRetry — resiliencia ante chunks obsoletos tras un deploy
// =====================================================================
// Problema (SPA + code-splitting): cuando desplegamos, `aws s3 sync --delete`
// borra los chunks del build anterior y sube unos con hash nuevo. Un usuario
// que dejó la pestaña abierta con el build viejo, al navegar a una ruta lazy
// que aún no había cargado, pide un chunk que ya no existe. CloudFront, por su
// fallback de SPA (404/403 → index.html con 200), devuelve el HTML de
// index.html en vez de un 404. El navegador intenta parsear ese HTML como JS y
// lanza `SyntaxError: Unexpected token '<'` (el `<` de `<!DOCTYPE html>`).
//
// Solución: si la carga dinámica falla, forzamos UNA recarga completa de la
// página para bajar el index.html nuevo (con los hashes de chunk correctos).
// Un flag en sessionStorage evita un bucle de recargas si el fallo persiste
// por otra causa (p. ej. offline): a la segunda vez propagamos el error al
// ErrorBoundary en lugar de recargar indefinidamente.
//
// ⚠️ La recarga tiene que ESQUIVAR LA CACHÉ, y esta es la parte que faltaba.
// `window.location.reload()` puede resolver index.html desde la caché del
// navegador —el deploy lo sube sin `Cache-Control`, así que aplica el cacheo
// heurístico—, y entonces vuelve el MISMO index.html viejo, con los mismos hashes
// muertos: falla otra vez, el flag ya está puesto y el error termina en el
// ErrorBoundary con la pantalla de "Algo salió mal". Es exactamente lo que se vio
// en Sentry (JAVASCRIPT-REACT-F, ChunkLoadError de un chunk CSS). Navegar a la
// misma URL con un parámetro nuevo obliga a pedir el documento al servidor.
// El arreglo de fondo es servir index.html con `no-cache` (ver DOCUMENTACION.md,
// comandos de deploy); esto lo hace funcionar igual mientras haya cachés vivas.
const RELOAD_FLAG = "chunk_reload_attempted";
const CACHE_BUST_PARAM = "_cb";

/** Vuelve a pedir el documento al servidor, sin pasar por la caché. */
function reloadBypassingCache() {
  const url = new URL(window.location.href);
  url.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
  // replace y no assign: no deja una entrada de historial, así que el "atrás" del
  // usuario sigue llevándolo a donde esperaba.
  window.location.replace(url.toString());
}

/** Saca el parámetro de la barra de direcciones una vez que ya cumplió su función. */
function cleanCacheBustParam() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(CACHE_BUST_PARAM)) return;
  url.searchParams.delete(CACHE_BUST_PARAM);
  window.history.replaceState(
    window.history.state,
    "",
    url.pathname + (url.search || "") + (url.hash || ""),
  );
}

export default function lazyWithRetry(importer) {
  return lazy(async () => {
    try {
      const component = await importer();
      // Carga OK → limpiamos el flag para que un futuro deploy vuelva a tener
      // derecho a una recarga, y borramos el parámetro de la URL.
      window.sessionStorage.removeItem(RELOAD_FLAG);
      cleanCacheBustParam();
      return component;
    } catch (err) {
      const alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG);
      if (!alreadyReloaded) {
        window.sessionStorage.setItem(RELOAD_FLAG, "1");
        reloadBypassingCache();
        // Devolvemos una promesa que nunca resuelve para que React mantenga el
        // fallback de Suspense mientras la recarga está en curso (evita mostrar
        // el ErrorBoundary por un instante).
        return new Promise(() => {});
      }
      // Ya recargamos una vez y sigue fallando → no es un chunk obsoleto.
      // Propagamos para que lo capture el ErrorBoundary / Sentry.
      throw err;
    }
  });
}
